import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface UserTokenStats {
  user_id: string;
  username: string;
  total_tokens: number;
  embedding_tokens: number;
  search_tokens: number;
  estimated_cost: number;
  call_count: number;
}

interface UserDetailRow {
  user_id: string;
  open: boolean;
}

const UserConsumptionPage: React.FC = () => {
  const [users, setUsers] = useState<UserTokenStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [userDetails, setUserDetails] = useState<{ [key: string]: any }>({});

  const getAuthToken = () => {
    return localStorage.getItem('accessToken') || localStorage.getItem('authToken') || localStorage.getItem('token') || '';
  };

  useEffect(() => {
    fetchUserConsumption();
  }, []);

  const fetchUserConsumption = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get consumption for last 30 days to aggregate by user
      const response = await fetch('/api/billing/consumption?days=30', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user consumption data');
      }

      const data = await response.json();
      console.log('📊 Consumption API response:', data);

      // Parse and aggregate user data
      const userMap: { [key: string]: UserTokenStats } = {};

      if (data.daily_breakdown && Array.isArray(data.daily_breakdown)) {
        console.log('📅 Found daily_breakdown with', data.daily_breakdown.length, 'days');
        for (const day of data.daily_breakdown) {
          console.log('  📆 Processing day:', day.date, 'services:', Object.keys(day.services || {}));
          for (const [serviceName, serviceData]: any of Object.entries(day.services || {})) {
            for (const [operationType, opData]: any of Object.entries(serviceData.operations || {})) {
              for (const [modelName, modelData]: any of Object.entries(opData.models || {})) {
                const userId = 'current_user'; // In this context, it's the current user
                if (!userMap[userId]) {
                  userMap[userId] = {
                    user_id: userId,
                    username: 'You',
                    total_tokens: 0,
                    embedding_tokens: 0,
                    search_tokens: 0,
                    estimated_cost: 0,
                    call_count: 0,
                  };
                }

                userMap[userId].total_tokens += Number(modelData.total_tokens) || 0;
                userMap[userId].estimated_cost += Number(modelData.cost_usd) || 0;
                userMap[userId].call_count += 1;

                if (operationType === 'embedding') {
                  userMap[userId].embedding_tokens += Number(modelData.total_tokens) || 0;
                } else if (operationType === 'semantic_search') {
                  userMap[userId].search_tokens += Number(modelData.total_tokens) || 0;
                }
              }
            }
          }
        }
      } else {
        console.log('⚠️ No daily_breakdown in response. Response keys:', Object.keys(data));
      }

      console.log('👥 Aggregated users:', Object.keys(userMap).length, 'users');
      setUsers(Object.values(userMap));
    } catch (err: any) {
      console.error('❌ Error fetching consumption:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserExpand = async (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
      // Fetch detailed breakdown if not already loaded
      if (!userDetails[userId]) {
        await fetchUserDetails(userId);
      }
    }
    setExpandedUsers(newExpanded);
  };

  const fetchUserDetails = async (userId: string) => {
    try {
      const response = await fetch('/api/billing/consumption?days=30', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📋 User details loaded:', data.daily_breakdown?.length || 0, 'days');
        setUserDetails((prev) => ({
          ...prev,
          [userId]: data.daily_breakdown || [],
        }));
      } else {
        console.error('❌ Failed to fetch user details:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('❌ Error fetching user details:', err);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {console.log('🎯 UserConsumptionPage rendered. Users:', users.length, 'Loading:', loading, 'Error:', error)}
      <Typography variant="h5" sx={{ mb: 2 }}>
        📊 User Token Consumption Overview
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <Card>
          <CardHeader title="Token Usage by User (Last 30 Days)" subheader={`${users.length} user(s)`} />
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell></TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Total Tokens</TableCell>
                    <TableCell align="right">Embedding Tokens</TableCell>
                    <TableCell align="right">Search Tokens</TableCell>
                    <TableCell align="right">Operations</TableCell>
                    <TableCell align="right">Est. Cost (USD)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No token consumption data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <React.Fragment key={user.user_id}>
                        <TableRow>
                          <TableCell sx={{ width: 50 }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleUserExpand(user.user_id)}
                            >
                              {expandedUsers.has(user.user_id) ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>{user.username}</TableCell>
                          <TableCell align="right">
                            {user.total_tokens.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {user.embedding_tokens.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {user.search_tokens.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">{user.call_count}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                            ${user.estimated_cost.toFixed(4)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                            <Collapse in={expandedUsers.has(user.user_id)} timeout="auto">
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                  Daily Breakdown
                                </Typography>
                                {userDetails[user.user_id] && userDetails[user.user_id].length > 0 ? (
                                  <TableContainer component={Paper}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                          <TableCell>Date</TableCell>
                                          <TableCell align="right">Service</TableCell>
                                          <TableCell align="right">Operation</TableCell>
                                          <TableCell align="right">Model</TableCell>
                                          <TableCell align="right">Tokens</TableCell>
                                          <TableCell align="right">Cost</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {userDetails[user.user_id].map(
                                          (dayData: any, dayIdx: number) => (
                                            <TableRow key={dayIdx}>
                                              <TableCell>{dayData.date}</TableCell>
                                              <TableCell colSpan={5}>
                                                {Object.entries(dayData.services || {}).map(
                                                  ([svc, svcData]: any) => (
                                                    <Box key={svc} sx={{ mb: 1 }}>
                                                      {Object.entries(svcData.operations || {}).map(
                                                        ([opType, opData]: any) => (
                                                          <Box
                                                            key={opType}
                                                            sx={{ pl: 2, mb: 0.5 }}
                                                          >
                                                            {Object.entries(opData.models || {}).map(
                                                              ([modelName, modelData]: any) => (
                                                                <Box
                                                                  key={modelName}
                                                                  sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    fontSize: '0.85rem',
                                                              }}
                                                                >
                                                                  <Typography variant="caption">
                                                                    {svc} / {opType} /{' '}
                                                                    {modelName}
                                                                  </Typography>
                                                                  <Typography
                                                                    variant="caption"
                                                                    sx={{ mr: 2 }}
                                                                  >
                                                                    {modelData.total_tokens.toLocaleString()} tokens
                                                                    {' | '}${modelData.cost_usd.toFixed(4)}
                                                                  </Typography>
                                                                </Box>
                                                              ),
                                                            )}
                                                          </Box>
                                                        ),
                                                      )}
                                                    </Box>
                                                  ),
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">
                                    No detailed breakdown available
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Data for last 30 days
              </Typography>
              <Button variant="outlined" size="small" onClick={fetchUserConsumption} disabled={loading}>
                🔄 Refresh
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default UserConsumptionPage;
